import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Form, Input, Popconfirm, Table, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./dailyReport.css";

const { Title } = Typography;

const EditableContext = React.createContext(null);

const formatListDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("M/D/YYYY") : String(value);
};

const stripMeta = ({ key, date, dailyReportId, ...fields }) => fields;

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({
        ...record,
        ...values,
      });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const buildRowsFromReport = (dailyReport, itemsKey) =>
  (dailyReport?.[itemsKey] || []).map((item, index) => ({
    ...item,
    key: `${dailyReport._id || "day"}-${index}`,
    date: dailyReport.date,
    dailyReportId: dailyReport._id,
  }));

const buildRowsFromRange = (rangeReports, itemsKey) => {
  const rows = [];
  (rangeReports || []).forEach((report) => {
    (report[itemsKey] || []).forEach((item, index) => {
      rows.push({
        ...item,
        key: `${report._id}-${index}`,
        date: report.date,
        dailyReportId: report._id,
      });
    });
  });
  return rows;
};

const NotesTable = ({
  dailyReport,
  updateDailyReportMutation,
  showDateColumn = false,
  rangeReports = null,
  rangeAddTarget = null,
  itemsKey = "notes",
  valueKey = "note",
  title = "Notes",
  columnTitle = "Note",
  addButtonLabel = "Add a Note",
}) => {
  const isListMode = showDateColumn && Array.isArray(rangeReports);
  const [dataSource, setDataSource] = useState(
    isListMode
      ? buildRowsFromRange(rangeReports, itemsKey)
      : buildRowsFromReport(dailyReport, itemsKey)
  );
  const [count, setCount] = useState(dailyReport?.[itemsKey]?.length || 0);

  useEffect(() => {
    if (isListMode) {
      setDataSource(buildRowsFromRange(rangeReports, itemsKey));
    } else {
      setDataSource(buildRowsFromReport(dailyReport, itemsKey));
      setCount(dailyReport?.[itemsKey]?.length || 0);
    }
  }, [dailyReport, rangeReports, isListMode, itemsKey]);

  const persistReportRows = (rows, reportId) => {
    const items = rows
      .filter((item) => item.dailyReportId === reportId)
      .map(stripMeta);
    updateDailyReportMutation({
      dailyReport: { [itemsKey]: items },
      dailyReportId: reportId,
    });
  };

  const handleDelete = (key) => {
    const row = dataSource.find((item) => item.key === key);
    const newData = dataSource.filter((item) => item.key !== key);
    setDataSource(newData);
    if (row?.dailyReportId) {
      persistReportRows(newData, row.dailyReportId);
    }
  };

  const defaultColumns = [
    ...(showDateColumn
      ? [
          {
            title: "Date",
            dataIndex: "date",
            width: "20%",
            render: (value) => formatListDate(value),
          },
        ]
      : []),
    {
      title: columnTitle,
      dataIndex: valueKey,
      width: showDateColumn ? "80%" : "100%",
      editable: true,
    },
    {
      dataIndex: "operation",
      render: (_, record) =>
        dataSource.length >= 1 ? (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => handleDelete(record.key)}
          >
            <Button
              danger
              shape="circle"
              type="text"
              icon={<CloseOutlined />}
            />
          </Popconfirm>
        ) : null,
    },
  ];

  const canAdd =
    (!isListMode && !!dailyReport?._id) ||
    (isListMode && !!rangeAddTarget?.id);

  const handleAdd = () => {
    if (!canAdd) return;
    const targetId = isListMode ? rangeAddTarget.id : dailyReport._id;
    const targetDate = isListMode ? rangeAddTarget.date : dailyReport.date;
    const newData = {
      key: `${targetId}-${count}-${Date.now()}`,
      [valueKey]: "",
      date: targetDate,
      dailyReportId: targetId,
    };
    const next = [...dataSource, newData];
    setDataSource(next);
    setCount(count + 1);
    persistReportRows(next, targetId);
  };

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
    if (row.dailyReportId) {
      persistReportRows(newData, row.dailyReportId);
    }
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div>
      <div className="flex justify-between mt-6">
        <Title level={3}>{title}</Title>
        {canAdd && (
          <Button onClick={handleAdd} type="primary" className="mb-4">
            {addButtonLabel}
          </Button>
        )}
      </div>
      <Table
        components={components}
        rowClassName={() => "editable-row"}
        bordered
        dataSource={dataSource}
        columns={columns}
        pagination={{ position: ["none", "none"] }}
      />
    </div>
  );
};
export default NotesTable;
